import React from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tr,
  Td,
  Th,
  Thead,
  Tbody,
  Text,
  VisuallyHidden,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiFileText,
  FiRotateCcw,
  FiTruck,
  FiUsers,
} from "react-icons/fi";

import { useQuery } from "@tanstack/react-query";

import { axiosClient } from "../../client/http-client";
import { getAuthToken } from "../../client/token-utils";
import {
  DataTable,
  DefinitionList,
  EmptyState,
  MotionReveal,
  StatusBadge,
} from "../ui";

interface RequestCreditApplicationViewModalProps {
  applicationId: any;
  isOpen: boolean;
  onClose: () => void;
}

export type Root = {
  id: number;
  account_id: string;
  branch_id: string;
  application_id: string;
  status: string;
  type: string;
  branch: {
    branch_id: string;
    branch_name: string;
  };
  data: {
    general: {
      type: string;
      label: string;
      data: {
        outlet_legal_name: string;
        outlet_trade_name: string;
        billing_outlet_address: string;
        billing_country: string;
        billing_city: string;
        billing_phone: string;
        billing_po_box: string;
        billing_nearest_landmark: string;
        delivery_outlet_address: string;
        delivery_country: string;
        delivery_city: string;
        delivery_phone: string;
        delivery_po_box: string;
        delivery_nearest_landmark: string;
        business_sector: string;
        website_url: string;
        service_years: number;
      };
    };
    license: {
      type: string;
      label: string;
      data: {
        license_number: string;
        vat_number: string;
        vat_expiration: string;
        license_expiration: string;
      };
    };
    contacts: {
      type: string;
      label: string;
      data: Array<{
        title: string;
        name: string;
        email: string;
        phone: string;
        mobile: string;
        shareholder_percentage: string;
      }>;
    };
    bank: {
      bank_name: string;
      bank_branch: string;
      bank_account_number: string;
      bank_iban?: string;
      bank_swift: string;
      bank_account_type: string;
    };

    suppliers: Array<{
      name: string;
      contact: string;
      phone: number;
      email?: string;
      address: string;
      designation: string;
    }>;
    requests: {
      type: string;
      label: string;
      data: {
        credit_limit: string;
        credit_period: string;
        payment_mode: any;
      };
    };
    documents: {
      type: string;
      label: string;
      data: Array<{
        id: number;
        fieldname: string;
        filename: string;
        originalname: string;
        mimetype: string;
        path: string;
        size: string;
      }>;
    };
  };
};

/** Human labels for the document field names the API returns. */
const DOCUMENT_LABELS: Record<string, string> = {
  license: "Trade licence",
  owner_pp: "Owner/Partner passport",
  owner_eid: "Owner/Partner EID",
  owner_visa: "Owner visa",
  vat: "VAT certificate",
  power_of_attorney: "Power of attorney",
  bank_certificate: "Bank certificate",
};

const documentLabel = (fieldname: string) =>
  DOCUMENT_LABELS[fieldname] ??
  fieldname.replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Read-only section: h3 (xs look) + hairline, then the section body (§7.4). */
const ViewSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box as="section">
    <Heading as="h3" size="xs">
      {title}
    </Heading>
    <Divider mt={3} mb={4} />
    {children}
  </Box>
);

/** Loading state: section headings with label/value bars in the same rhythm as the loaded view. */
const ViewSkeleton: React.FC = () => (
  <Stack spacing={8} aria-busy="true">
    <VisuallyHidden>Loading</VisuallyHidden>
    {[6, 4, 4].map((rows, s) => (
      <Box key={s}>
        <Skeleton speed={1.2} h="1rem" w="32%" />
        <Divider mt={3} mb={4} />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={0}>
          {Array.from({ length: rows }).map((_, i) => (
            <Box
              key={i}
              py={3}
              borderBottom="1px solid"
              borderColor="border.default"
            >
              <Skeleton speed={1.2} h="0.625rem" w="30%" mb={2} />
              <Skeleton speed={1.2} h="0.875rem" w={`${[70, 45, 60, 50][i % 4]}%`} />
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    ))}
  </Stack>
);

export const RequestCreditApplicationViewModal: React.FC<
  RequestCreditApplicationViewModalProps
> = ({ applicationId, isOpen, onClose }) => {
  const token = getAuthToken();

  // get Selected application data
  const getSelectedApplication = async (applicationId: string) => {
    const response = await axiosClient.get(
      `/applications/${applicationId}/requestcredit`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response;
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["selectedApplication", applicationId],
    queryFn: () => getSelectedApplication(applicationId),
    enabled: !!applicationId,
  });

  const application = data?.data;
  const status: string | undefined = application?.status;
  // Guard every section: a saved-progress payload may not contain all of them yet.
  const general = application?.data?.general?.data;
  const license = application?.data?.license?.data;
  const contacts = application?.data?.contacts?.data ?? [];
  const bank = application?.data?.bank?.data;
  const suppliers = application?.data?.suppliers?.data ?? [];
  const requests = application?.data?.requests?.data;
  const documents = application?.data?.documents?.data ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"3xl"}
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text textStyle="label" color="text.muted" aria-hidden>
            APPLICATION · REQUEST CREDIT
          </Text>
          <Flex
            align="center"
            gap={3}
            wrap="wrap"
            mt={1}
            pr={{ base: 8, md: 6 }}
          >
            {isPending ? (
              <Skeleton speed={1.2} h="1.5rem" w="12rem" />
            ) : (
              <>
                <Box as="h2" noOfLines={1} minW={0}>
                  {general?.outlet_legal_name ||
                    application?.application_id ||
                    "Credit account application"}
                </Box>
                {status && <StatusBadge kind="application" value={status} />}
              </>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton aria-label="Close" />
        <ModalBody>
          {isPending ? (
            <ViewSkeleton />
          ) : isError ? (
            <Alert status="error" alignItems="flex-start">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>We couldn't load this application</AlertTitle>
              </Box>
              <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          ) : (
            <MotionReveal>
              <Stack spacing={8}>
                {/* Same grouping as the branch view: addresses are eyebrowed lists inside General information. */}
                <ViewSection title="General information">
                  <Stack spacing={5}>
                    <DefinitionList
                      columns={2}
                      items={[
                        {
                          label: "Outlet Legal Name",
                          value: general?.outlet_legal_name,
                        },
                        {
                          label: "Outlet Trade Name",
                          value: general?.outlet_trade_name,
                        },
                        {
                          label: "Business Sector",
                          value: general?.business_sector,
                        },
                        {
                          label: "Service Years",
                          value: general?.service_years,
                        },
                        {
                          label: "Website URL",
                          value: general?.website_url,
                        },
                      ]}
                    />
                    <DefinitionList
                      eyebrow="BILLING ADDRESS"
                      columns={2}
                      items={[
                        {
                          label: "Billing Outlet Address",
                          value:
                            general?.billing_outlet_address,
                        },
                        {
                          label: "Billing Country",
                          value: general?.billing_country,
                        },
                        {
                          label: "Billing City",
                          value: general?.billing_city,
                        },
                        {
                          label: "Billing Phone",
                          value: general?.billing_phone,
                        },
                        {
                          label: "Billing PO Box",
                          value: general?.billing_po_box,
                        },
                        {
                          label: "Billing Nearest Landmark",
                          value:
                            general?.billing_nearest_landmark,
                        },
                      ]}
                    />
                    <DefinitionList
                      eyebrow="DELIVERY ADDRESS"
                      columns={2}
                      items={[
                        {
                          label: "Delivery Outlet Address",
                          value:
                            general?.delivery_outlet_address,
                        },
                        {
                          label: "Delivery Country",
                          value: general?.delivery_country,
                        },
                        {
                          label: "Delivery City",
                          value: general?.delivery_city,
                        },
                        {
                          label: "Delivery Phone",
                          value: general?.delivery_phone,
                        },
                        {
                          label: "Delivery PO Box",
                          value: general?.delivery_po_box,
                        },
                        {
                          label: "Delivery Nearest Landmark",
                          value:
                            general?.delivery_nearest_landmark,
                        },
                      ]}
                    />
                  </Stack>
                </ViewSection>

                <ViewSection title="Licence information">
                  <DefinitionList
                    columns={2}
                    items={[
                      {
                        label: "Licence Number",
                        value: license?.license_number,
                      },
                      {
                        label: "Licence Expiration",
                        value:
                          license?.license_expiration,
                      },
                      {
                        label: "VAT Number",
                        value: license?.vat_number,
                      },
                      {
                        label: "VAT Expiration",
                        value: license?.vat_expiration,
                      },
                    ]}
                  />
                </ViewSection>

                <ViewSection title="Contacts">
                  {contacts.length > 0 ? (
                    <DataTable size="sm" label="Contacts table">
                      <Table>
                        <Thead>
                          <Tr>
                            <Th scope="col">Title</Th>
                            <Th scope="col">Name</Th>
                            <Th scope="col">Email</Th>
                            <Th scope="col">Phone</Th>
                            <Th scope="col">Mobile</Th>
                            <Th scope="col" isNumeric>
                              Shareholder Percentage
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {contacts.map(
                            (
                              user: {
                                title: string;
                                name: string;
                                email: string;
                                phone: number;
                                mobile: number;
                                shareholder_percentage: number;
                              },
                              index: number
                            ) => (
                              <Tr key={index}>
                                <Td>{user.title}</Td>
                                <Td fontWeight={600}>{user.name}</Td>
                                <Td>{user.email}</Td>
                                <Td fontWeight={600}>{user.phone}</Td>
                                <Td fontWeight={600}>{user.mobile}</Td>
                                <Td isNumeric>{user.shareholder_percentage}</Td>
                              </Tr>
                            )
                          )}
                        </Tbody>
                      </Table>
                    </DataTable>
                  ) : (
                    <EmptyState
                      icon={FiUsers}
                      title="No contacts added"
                      py={6}
                      headingAs="p"
                    />
                  )}
                </ViewSection>

                <ViewSection title="Bank information">
                  <DefinitionList
                    columns={2}
                    items={[
                      {
                        label: "Bank Name",
                        value: bank?.bank_name,
                      },
                      {
                        label: "Bank Branch",
                        value: bank?.bank_branch,
                      },
                      {
                        label: "Bank Account Number",
                        value: bank?.bank_account_number,
                      },
                      {
                        label: "Bank IBAN",
                        value: bank?.bank_iban,
                      },
                      {
                        label: "Bank Swift",
                        value: bank?.bank_swift,
                      },
                      {
                        label: "Bank Account Type",
                        value: bank?.bank_account_type,
                      },
                    ]}
                  />
                </ViewSection>

                <ViewSection title="Supplier references">
                  {suppliers.length > 0 ? (
                    <DataTable size="sm" label="Supplier references table">
                      <Table>
                        <Thead>
                          <Tr>
                            <Th scope="col">Name</Th>
                            <Th scope="col">Contact</Th>
                            <Th scope="col">Address</Th>
                            <Th scope="col">Designation</Th>
                            <Th scope="col">Phone</Th>
                            <Th scope="col">Email</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {suppliers.map(
                            (
                              user: {
                                name: string;
                                contact: string;
                                address: string;
                                designation: string;
                                phone: number;
                                email: string;
                              },
                              index: number
                            ) => (
                              <Tr key={index}>
                                <Td fontWeight={600}>{user.name}</Td>
                                <Td>{user.contact}</Td>
                                <Td>{user.address}</Td>
                                <Td>{user.designation}</Td>
                                <Td fontWeight={600}>{user.phone}</Td>
                                <Td>{user.email}</Td>
                              </Tr>
                            )
                          )}
                        </Tbody>
                      </Table>
                    </DataTable>
                  ) : (
                    <EmptyState
                      icon={FiTruck}
                      title="No suppliers added"
                      py={6}
                      headingAs="p"
                    />
                  )}
                </ViewSection>

                <ViewSection title="Credit request">
                  <DefinitionList
                    columns={2}
                    items={[
                      {
                        label: "Credit Limit",
                        value: requests?.credit_limit,
                      },
                      {
                        label: "Credit Period",
                        value: requests?.credit_period,
                      },
                      {
                        label: "Payment Mode",
                        value: requests?.payment_mode,
                      },
                    ]}
                  />
                </ViewSection>

                <ViewSection title="Documents">
                  {documents.length > 0 ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      {documents.map((document: any) => (
                        <Flex
                          key={document.id}
                          align="center"
                          gap={3}
                          p={3}
                          borderRadius="md"
                          bg="surface.sunken"
                          minW={0}
                        >
                          <Flex
                            w="36px"
                            h="36px"
                            flexShrink={0}
                            borderRadius="full"
                            bg="leaf.100"
                            color="leaf.700"
                            align="center"
                            justify="center"
                            aria-hidden
                          >
                            <Icon as={FiFileText} boxSize="16px" />
                          </Flex>
                          <Box flex={1} minW={0}>
                            <Text textStyle="body.sm" fontWeight={700} noOfLines={1}>
                              {documentLabel(document.fieldname)}
                            </Text>
                            <Text
                              textStyle="caption"
                              color="text.muted"
                              noOfLines={1}
                              title={document.originalname}
                            >
                              {document.originalname || "Uploaded"}
                            </Text>
                          </Box>
                          <Icon
                            as={FiCheckCircle}
                            boxSize="16px"
                            color="leaf.600"
                            flexShrink={0}
                            aria-hidden
                          />
                          <VisuallyHidden>Uploaded</VisuallyHidden>
                        </Flex>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <EmptyState
                      icon={FiFileText}
                      title="No documents uploaded"
                      py={6}
                      headingAs="p"
                    />
                  )}
                </ViewSection>
              </Stack>
            </MotionReveal>
          )}
        </ModalBody>

        <ModalFooter justifyContent="flex-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
